﻿import * as React from "react";
import * as dtos from "dtos";
import { getLocalizedDate } from "@modules/utils/dates";

export type ItemData = 
    | {
        type: "trigger-record"
        data: dtos.ITriggerRecordDTO
    }
    | { 
        type: "stage-record"
        data: dtos.IStageRecordDTO
    }
    | {
        type: "archive-record"
        data: dtos.IArchiveRecordDTO
    }
    | {
        type: "creation-date"
        date: Date | string
    };
    
type Props = { index: number; record: ItemData; showSource: boolean; };

export function TimelineItem(props: Props): JSX.Element {
    let recordDate: string | number | Date;
    let iconClass: string;
    let bgColor: string;
    let message: string;
    let key: string;

    switch (props.record.type) {
        case "archive-record":
            if (props.record.data.changeType === "archived")
            {
                message = "Order closed and archived.";
                bgColor = "#526273";
                iconClass = "fa-archive";
                key = "archive-date";
                recordDate = props.record.data.date;
            }
            else
            {
                message = "Order opened and unarchived.";
                bgColor = "mediumturquoise";
                iconClass = "fa-folder-open";
                key = "unarchive-date";
                recordDate = props.record.data.date;
            }

        	break;

        case "creation-date":
        	message = "Order received.";
        	iconClass = "fa-flash";
        	bgColor = "green";
        	key = "received-date";
        	recordDate = props.record.date;
        	break;

        case "trigger-record":
            message = props.record.data.description;
            iconClass = "fa-envelope";
            bgColor = "#2e6da4";
            key = props.index.toString();
            recordDate = props.record.data.date;
            break;

        case "stage-record":
            message = `Stage changed from ${props.record.data.originStage.name} to ${props.record.data.targetStage.name}.`;
            iconClass = "fa-magic";
            bgColor = props.record.data.targetStage.hexColor;
            key = props.index.toString();
            recordDate = props.record.data.date;
            break;
            
        default:
            const t: never = props.record;
            console.error("Unrecognized record type on props", props);
            throw new Error("Unrecognized record type");
    }
    let source: JSX.Element | null = null;

    if (props.showSource && (props.record.type === "trigger-record" || props.record.type === "archive-record" || props.record.type === "stage-record")) {
        let sourceName: string;

        switch (props.record.data.source.type) {
            case "user":
                sourceName = props.record.data.source.userFullName;
                break;

            case "shopify_webhook":
                sourceName = "Shopify Webhook Event";
                break;

            case "deleted_user":
                sourceName = "(by a deleted team member)";
                break;
            
            case "grandfathered":
                sourceName = "(occurred before source user tracking was implemented)";
                break;

            default: 
                const source: never = props.record.data.source;
                console.error("Unhandled record source type", { source: source });
                sourceName = "(failed to determine event source)";
                break;
        }


        source = (
            <p className="timeline-source">
                <small className="text-muted">
                    <i className="fa fa-user marRight5 color"/>
                    {sourceName}
                </small>
            </p>
        )
    }

    return (
        <li key={key} className={props.index % 2 !== 0 ? "timeline-inverted" : ""}>
            <div className="timeline-badge" style={{ backgroundColor: bgColor }}>
                <i className={"fa " + iconClass}/>
            </div>
            <div className="timeline-panel">
                <div className="timeline-heading">
                    <h4 className="timeline-title">{message}</h4>
                    <p>
                        <small className="text-muted">
                            <i className="fa fa-clock-o marRight5 color"/>
                            {getLocalizedDate(new Date(recordDate), false)}
                        </small>
                    </p>
                    {source}
                </div>
            </div>
        </li>
    );
}
